import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneTilt } from "@phosphor-icons/react";

const STATUSES = [
  "new", "reviewing", "contacted", "qualified",
  "consultation_requested", "consultation_booked", "won", "not_now", "closed",
];

export default function LeadDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/leads/${id}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const updateStatus = async (status) => {
    setSavingStatus(true);
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Status updated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSavingStatus(false); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/admin/leads/${id}/activities`, { type: "note", notes: newNote });
      setNewNote("");
      toast.success("Note added");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const [invitingIdeaLab, setInvitingIdeaLab] = useState(false);
  const sendIdeaLabInvite = async () => {
    const email = data?.lead?.email;
    if (!email) return;
    if (!window.confirm(`Send the IdeaLab discovery invite to ${email}?`)) return;
    setInvitingIdeaLab(true);
    try {
      await api.post(`/admin/leads/${id}/send-idealab-invite`, { custom_note: "" });
      toast.success(`IdeaLab invite sent to ${email}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send invite.");
    } finally {
      setInvitingIdeaLab(false);
    }
  };

  if (loading || !data) return <div className="p-10 font-mono text-sm text-zinc-500" data-testid="lead-detail-loading">Loading…</div>;

  const lead = data.lead;

  return (
    <div className="p-8 md:p-10" data-testid="admin-lead-detail">
      <Link to="/admin/leads" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-6" data-testid="lead-back">
        <ArrowLeft size={14} /> All leads
      </Link>

      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <p className="overline mb-2">{lead.source}</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{lead.name || lead.email}</h1>
          <p className="text-zinc-600 mt-1">{lead.email}{lead.company ? ` · ${lead.company}` : ""}</p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <button
            onClick={sendIdeaLabInvite}
            disabled={invitingIdeaLab || !lead.email}
            className="btn-signal !py-2 disabled:opacity-50"
            data-testid="lead-send-idealab-invite"
            title="Email this lead a direct link to start the IdeaLab discovery session"
          >
            <PaperPlaneTilt size={14} weight="fill" /> {invitingIdeaLab ? "Sending…" : "Send IdeaLab invite"}
          </button>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Status</label>
            <select value={lead.status} onChange={(e) => updateStatus(e.target.value)} disabled={savingStatus} className="border border-zinc-200 px-3 py-2 text-sm bg-white" data-testid="lead-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Lead details">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <Dt label="Phone">{lead.phone || "—"}</Dt>
              <Dt label="Role">{lead.role || "—"}</Dt>
              <Dt label="Sector">{lead.sector || "—"}</Dt>
              <Dt label="Website">{lead.website || "—"}</Dt>
              <Dt label="Interest area">{lead.interest_area || "—"}</Dt>
              <Dt label="Timeframe">{lead.timeframe || "—"}</Dt>
              <Dt label="Budget">{lead.budget || "—"}</Dt>
              <Dt label="Consultation">{lead.consultation_flag ? "Yes" : "No"}</Dt>
            </dl>
            <div className="mt-6 space-y-4">
              <Dt label="Pain point" block>{lead.pain_point || "—"}</Dt>
              <Dt label="Desired outcome" block>{lead.desired_outcome || "—"}</Dt>
              <Dt label="Notes" block>{lead.notes || "—"}</Dt>
            </div>
          </Card>

          <Card title="Activity">
            <div className="mb-4 flex gap-2">
              <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note or log an activity…" className="flex-1 border border-zinc-200 px-3 py-2 text-sm" data-testid="lead-note-input" />
              <button onClick={addNote} className="btn-signal text-sm" data-testid="lead-note-add">Add</button>
            </div>
            {data.activities.length === 0 ? (
              <p className="text-sm text-zinc-500 font-mono">No activity yet.</p>
            ) : (
              <ul className="space-y-4" data-testid="lead-activity-list">
                {data.activities.map((a) => (
                  <li key={a.id} className="border-l-2 border-zinc-200 pl-4">
                    <div className="flex items-center gap-2 text-xs font-mono text-zinc-500 mb-1">
                      <span className="uppercase tracking-widest">{a.type}</span>
                      <span>·</span>
                      <span>{a.timestamp ? new Date(a.timestamp).toLocaleString() : ""}</span>
                    </div>
                    <p className="text-sm text-zinc-800">{a.notes}</p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Consent">
            <dl className="text-sm space-y-2">
              <Dt label="Privacy">{lead.consent_privacy ? "Given" : "Not given"}</Dt>
              <Dt label="Marketing">{lead.consent_marketing ? "Given" : "Not given"}</Dt>
            </dl>
          </Card>
          <Card title="Meta">
            <dl className="text-sm space-y-2">
              <Dt label="Created">{lead.created_at ? new Date(lead.created_at).toLocaleString() : "—"}</Dt>
              <Dt label="Updated">{lead.updated_at ? new Date(lead.updated_at).toLocaleString() : "—"}</Dt>
              <Dt label="Source"><span className="font-mono">{lead.source || "—"}</span></Dt>
              {lead.referring_demo && <Dt label="Demo"><span className="font-mono">{lead.referring_demo}</span></Dt>}
              {lead.promo_code && <Dt label="Promo code"><span className="font-mono text-[hsl(var(--appistry-signal))]">{lead.promo_code}</span></Dt>}
              <Dt label="IP">{lead.ip || "—"}</Dt>
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-zinc-200">
      <div className="px-6 py-4 border-b border-zinc-200"><h2 className="text-sm font-semibold uppercase tracking-widest font-mono text-zinc-600">{title}</h2></div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Dt({ label, children, block }) {
  return (
    <div className={block ? "" : "flex justify-between gap-4"}>
      <dt className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</dt>
      <dd className="text-sm text-zinc-800 whitespace-pre-wrap">{children}</dd>
    </div>
  );
}
